import { useEffect, useCallback } from 'react';
import type {
  UseFormReset,
  UseFormSetValue,
  UseFormTrigger,
  FieldValues,
  Path,
} from 'react-hook-form';
import type { IntakeResponse } from '../../services/api';
import type { IntakeRecord } from '../../store/slices/intakeSlice';
import {
  formatPhone,
  formatSSN,
  formatZipCode,
  normalizeGender,
} from '../../utils/intake';
import { getBusinessToday, getDayAfterDateStr } from '../../utils/dateUtils';
import { createFuzzyMatcher } from '../../utils/fuzzyMatch';
import {
  KINNSER_ADMISSION_SOURCE_VALUES,
  LANGUAGE_OPTIONS,
  OTHER_LANGUAGE_KEY,
} from './constants';
import type { CreatePatientFormData } from './types';

/**
 * Prefill for the push tab's Admission Source select.
 *
 * This precedence MUST mirror `_transform_intake` in
 * `backend/app/services/kinnser/service.py`. Initialization is read-only, so
 * an untouched prefill is never autosaved and the column stays null; the
 * server repeats the same fallback so the push sends what the screen showed
 * rather than null.
 *
 * A saved value wins, including the empty string, which is a deliberate clear
 * and must not fall back. Otherwise the intake's captured code is offered.
 *
 * Either way the result is prefilled only when WellSky can represent it. The
 * select has no option for anything else, so handing it such a value renders a
 * blank control that looks untouched while the server drops the same value
 * from the push -- the screen and the push would disagree with nothing on
 * screen to say so. Returning null here instead puts the form in the state the
 * push tab already explains: empty select, plus the notice naming what intake
 * captured. `IntakeUpdate.validate_kinnser_admission_source` should keep an
 * unsendable override from ever being persisted; this is the read-side half of
 * that guarantee, for rows written before it existed or by another writer.
 */
export function resolveKinnserAdmissionSourcePrefill(
  intake: IntakeResponse
): string | null {
  const saved = intake.kinnser_admission_source;
  if (saved !== null && saved !== undefined) {
    // String(): the column is typed as a string but arrives as untyped JSON,
    // and the backend mirror coerces before its own membership test.
    const value = String(saved);
    return KINNSER_ADMISSION_SOURCE_VALUES.has(value) ? value : null;
  }

  const captured = String(
    intake.fields?.discharge_facility_type ??
      intake.discharge_facility_type ??
      ''
  );
  return KINNSER_ADMISSION_SOURCE_VALUES.has(captured) ? captured : null;
}

// Default SOC/episode date: tomorrow on the agency's business calendar, as
// YYYY-MM-DD.
const getDefaultSocDate = (): string => {
  const today = getBusinessToday();
  return getDayAfterDateStr(today) ?? today;
};

/** True if a (YYYY-MM-DD) is on or before b. */
function isOnOrBefore(a: string | null | undefined, b: string | null): boolean {
  if (!a || !b) return false;
  return a <= b;
}

/** Normalize date string to YYYY-MM-DD for comparison. */
function toDateOnly(s: string | null | undefined): string | null {
  if (s == null || typeof s !== 'string') return null;
  const trimmed = s.trim().split('T')[0];
  return trimmed || null;
}

/**
 * Return only updates that differ from current intake values.
 * Prevents save→setSelectedIntake→effect re-run→save loop when intake already has the data.
 */
function filterActualUpdates<T extends Record<string, unknown>>(
  updates: T,
  intake: Record<string, unknown>
): Partial<T> {
  const result: Partial<T> = {};
  const dateOnlyKeys = new Set([
    'kinnser_soc_date',
    'kinnser_episode_start_date',
    'soc_date',
    'episode_start_date',
  ]);
  for (const [key, value] of Object.entries(updates) as [
    keyof T,
    T[keyof T],
  ][]) {
    const intakeVal = intake[key as string];
    if (dateOnlyKeys.has(key as string)) {
      if (
        toDateOnly(String(value ?? '')) !== toDateOnly(String(intakeVal ?? ''))
      ) {
        result[key] = value;
      }
    } else if (value !== intakeVal) {
      result[key] = value;
    }
  }
  return result;
}

/** Intake shape with matched episode date fields (from fields JSONB). */
type IntakeWithMatchedDates = IntakeResponse & {
  kinnser_matched_start_of_care?: string | null;
  kinnser_matched_discharge_date?: string | null;
  kinnser_matched_episode_start_date?: string | null;
  kinnser_matched_episode_end_date?: string | null;
};

/**
 * For existing patients with matched episode dates, return the minimum allowed
 * SOC date (one day after the latest of matched start of care / discharge).
 * Return null if no matched dates.
 */
export function getMinSocDateForMatchedPatient(
  intake: IntakeWithMatchedDates
): string | null {
  const soc = intake.kinnser_matched_start_of_care ?? null;
  const discharge = intake.kinnser_matched_discharge_date ?? null;
  if (!soc && !discharge) return null;
  const dayAfterSoc = getDayAfterDateStr(soc ?? '');
  const dayAfterDischarge = getDayAfterDateStr(discharge ?? '');
  if (!dayAfterSoc && !dayAfterDischarge) return null;
  if (!dayAfterSoc) return dayAfterDischarge;
  if (!dayAfterDischarge) return dayAfterSoc;
  return dayAfterSoc >= dayAfterDischarge ? dayAfterSoc : dayAfterDischarge;
}

/**
 * For existing patients with matched episode dates, return the minimum allowed
 * episode start date (one day after the latest of matched discharge, start of
 * care, prior episode start date, and matched episode end date).
 * Return null if no matched dates.
 */
export function getMinEpisodeStartDateForMatchedPatient(
  intake: IntakeWithMatchedDates
): string | null {
  const soc = intake.kinnser_matched_start_of_care ?? null;
  const discharge = intake.kinnser_matched_discharge_date ?? null;
  const priorStart = intake.kinnser_matched_episode_start_date ?? null;
  const priorEnd = intake.kinnser_matched_episode_end_date ?? null;
  if (!soc && !discharge && !priorStart && !priorEnd) return null;
  const dayAfterSoc = getDayAfterDateStr(soc ?? '');
  const dayAfterDischarge = getDayAfterDateStr(discharge ?? '');
  const dayAfterPriorStart = getDayAfterDateStr(priorStart ?? '');
  const dayAfterPriorEnd = getDayAfterDateStr(priorEnd ?? '');
  const candidates = [
    dayAfterSoc,
    dayAfterDischarge,
    dayAfterPriorStart,
    dayAfterPriorEnd,
  ].filter((d): d is string => d != null);
  if (candidates.length === 0) return null;
  return candidates.reduce((a, b) => (a >= b ? a : b));
}

interface UseFormInitializationOptions<TFieldValues extends FieldValues> {
  selectedIntake: IntakeRecord | null;
  reset: UseFormReset<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  trigger: UseFormTrigger<TFieldValues>;
  saveToServer: (updates: Partial<TFieldValues>) => Promise<void>;
}

/**
 * Hook for initializing and resetting the form with intake data.
 * Handles form population, formatting, and language fuzzy matching.
 * Generic to work with any form schema that is a subset of CreatePatientFormData.
 */
export function useFormInitialization<TFieldValues extends FieldValues>({
  selectedIntake,
  reset,
  setValue,
  trigger,
  saveToServer,
}: UseFormInitializationOptions<TFieldValues>): void {
  // Helper to safely set a value only if the field exists in the form schema
  const safeSetValue = useCallback(
    (fieldName: string, value: unknown) => {
      try {
        // Cast value to the expected type for setValue
        setValue(
          fieldName as Path<TFieldValues>,
          value as TFieldValues[Path<TFieldValues>]
        );
      } catch {
        // Field doesn't exist in this form schema, skip it
      }
    },
    [setValue]
  );

  // Helper to build form values with only fields that exist in the target schema
  const buildFormValues = useCallback(
    (
      intake: IntakeResponse,
      formattedPatientFields: Record<string, unknown>
    ) => {
      // Build the full set of possible values
      const allValues: Partial<CreatePatientFormData> = {
        // Clinic selection (form stores agency_clinic_id UUID for dropdown binding)
        agency_clinic_id: intake.agency_clinic_id || null,
        kinnser_physician_id: intake.kinnser_physician_id || null,
        kinnser_branch_id: intake.kinnser_branch_id || null,
        kinnser_insurance_provider_id:
          intake.agency_insurance?.insurance_key ||
          intake.kinnser_insurance_provider_id ||
          intake.agency_insurance_id ||
          null,
        // Read only. Initialization never triggers the external-referral
        // auto-select, which fires on a user pick alone.
        kinnser_internal_referral_id:
          intake.kinnser_internal_referral_id || null,
        kinnser_external_referral_id:
          intake.kinnser_external_referral_id || null,
        kinnser_admission_type: intake.kinnser_admission_type || null,
        kinnser_admission_source: resolveKinnserAdmissionSourcePrefill(intake),
        kinnser_hcpcs_code_key: intake.kinnser_hcpcs_code_key || null,
        kinnser_mrn: intake.kinnser_mrn || null,
        kinnser_hh_cahps_opt_out: intake.kinnser_hh_cahps_opt_out || null,
        kinnser_language_key:
          (intake as IntakeResponse & { kinnser_language_key?: string })
            .kinnser_language_key || null,
        kinnser_other_language:
          (intake as IntakeResponse & { kinnser_other_language?: string })
            .kinnser_other_language || null,

        // Kinnser Episode fields
        kinnser_create_episode: intake.kinnser_create_episode || null,
        kinnser_type_of_soc: intake.kinnser_type_of_soc || null,
        kinnser_soc_date: (() => {
          const min = getMinSocDateForMatchedPatient(intake);
          const val = intake.kinnser_soc_date;
          if (min && (!val || isOnOrBefore(val, min))) return min;
          return val;
        })(),
        kinnser_episode_start_date: (() => {
          const ext = intake as IntakeResponse & {
            kinnser_creation_type?:
              'new_admission' | 'new_episode' | 'current_episode' | null;
            kinnser_visit_type?: 'soc' | 'roc' | 'rec' | null;
            is_new_patient?: boolean | null;
          };
          const isExistingSocNewAdmission =
            ext.is_new_patient === false &&
            ext.kinnser_visit_type === 'soc' &&
            ext.kinnser_creation_type === 'new_admission';
          if (isExistingSocNewAdmission) {
            const minSoc = getMinSocDateForMatchedPatient(intake);
            const socVal = intake.kinnser_soc_date;
            const resolvedSoc =
              minSoc && (!socVal || isOnOrBefore(socVal, minSoc))
                ? minSoc
                : socVal;
            if (resolvedSoc != null && resolvedSoc !== '') {
              return resolvedSoc;
            }
          }
          const min = getMinEpisodeStartDateForMatchedPatient(intake);
          const val = intake.kinnser_episode_start_date;
          if (min && val && isOnOrBefore(val, min)) return min;
          return val;
        })(),
        kinnser_clinician_id: intake.kinnser_clinician_id || null,
        kinnser_clinical_care_manager_id:
          intake.kinnser_clinical_care_manager_id || null,
        kinnser_case_manager_id: intake.kinnser_case_manager_id || null,
        kinnser_oasis_approver_id: intake.kinnser_oasis_approver_id || null,
        kinnser_pt_approver_id: intake.kinnser_pt_approver_id || null,
        kinnser_ot_approver_id: intake.kinnser_ot_approver_id || null,
        kinnser_st_approver_id: intake.kinnser_st_approver_id || null,
        kinnser_primary_scheduler_id:
          intake.kinnser_primary_scheduler_id || null,
        kinnser_secondary_scheduler_id:
          intake.kinnser_secondary_scheduler_id || null,

        // Roger Episode fields
        roger_create_episode: intake.roger_create_episode || null,
        roger_type_of_soc: intake.roger_type_of_soc || null,
        roger_clinician_id: intake.roger_clinician_id || null,
        roger_case_manager_id: intake.roger_case_manager_id || null,
        clinic_physician_id: intake.clinic_physician_id || null,
        internal_referral_source_id: intake.internal_referral_source_id || null,
        soc_date: (() => {
          const min = getMinSocDateForMatchedPatient(intake);
          const val = intake.soc_date;
          if (min && (!val || isOnOrBefore(val, min))) return min;
          return val;
        })(),
        episode_start_date: (() => {
          // Soft-migration: prefer the shared `kinnser_episode_start_date`
          // column, fall back to the legacy Roger-only `episode_start_date`
          // for intakes written before the dual-write landed.
          const min = getMinSocDateForMatchedPatient(intake);
          const val =
            intake.kinnser_episode_start_date ?? intake.episode_start_date;
          if (min && val && isOnOrBefore(val, min)) return min;
          return val;
        })(),

        // Push to EMR flags (backward compat: if only old Kinnser flags set, treat as push_to_kinnser)
        push_to_roger: intake.push_to_roger || null,
        push_to_kinnser:
          intake.push_to_kinnser ??
          (intake.kinnser_create_episode === true ||
          (
            intake as IntakeResponse & {
              kinnser_visit_type?: 'soc' | 'roc' | 'rec' | null;
            }
          ).kinnser_visit_type === 'roc'
            ? true
            : null),

        // Kinnser scheduling: visit type and creation type (from intake.fields or derived)
        kinnser_visit_type:
          (
            intake as IntakeResponse & {
              kinnser_visit_type?: 'soc' | 'roc' | 'rec' | null;
            }
          ).kinnser_visit_type ??
          (intake.kinnser_create_episode === true ? 'soc' : null),
        // ROC only has one option: auto-select "Current episode" when visit type is ROC
        kinnser_creation_type: (() => {
          const visitType =
            (
              intake as IntakeResponse & {
                kinnser_visit_type?: 'soc' | 'roc' | 'rec' | null;
              }
            ).kinnser_visit_type ??
            (intake.kinnser_create_episode === true ? 'soc' : null);
          const fromIntake =
            (
              intake as IntakeResponse & {
                kinnser_creation_type?:
                  'new_admission' | 'new_episode' | 'current_episode' | null;
              }
            ).kinnser_creation_type ?? null;
          if (visitType === 'roc') return fromIntake || 'current_episode';
          return fromIntake;
        })(),
        roc_date: intake.roc_date ?? null,
        // Type of ROC (EMR-1272). No client-side default here: when ROC is the
        // active visit type and this is unset, useSchedulingStepForm sets AND
        // persists SN so the displayed default exists server-side.
        roc_type:
          (
            intake as IntakeResponse & {
              roc_type?: 'SN' | 'PT' | 'OT' | 'ST' | null;
            }
          ).roc_type ?? null,
        recert_date: intake.recert_date ?? null,

        // Push to EMR onboarding fields
        has_transferred_from_another_hha:
          intake.has_transferred_from_another_hha ?? null,
        prior_hha: intake.prior_hha ?? null,
        f2f_encounter_date: intake.f2f_encounter_date ?? null,

        // Patient Identification
        patient_first_name: intake.patient_first_name || null,
        patient_middle_name: intake.patient_middle_name || null,
        patient_last_name: intake.patient_last_name || null,
        patient_suffix: intake.patient_suffix || null,
        patient_dob: intake.patient_dob || null,
        // Normalize gender to M/F/U (handles legacy Male/Female/Unknown values)
        patient_gender: normalizeGender(intake.patient_gender),
        patient_ssn: intake.patient_ssn || null,
        patient_primary_language: intake.patient_primary_language || null,

        // Contact Information
        patient_phone: intake.patient_phone || null,
        patient_secondary_phone: intake.patient_secondary_phone || null,
        patient_email: intake.patient_email || null,
        patient_street_address: intake.patient_street_address || null,
        patient_city: intake.patient_city || null,
        patient_state: intake.patient_state || null,
        patient_zip_code: intake.patient_zip_code || null,
        patient_county: intake.patient_county || null,

        // Emergency contact (optional; pushed to WellSky when create/onboard)
        emergency_contact_name: intake.emergency_contact_name ?? null,
        emergency_contact_relationship:
          intake.emergency_contact_relationship ?? null,
        emergency_contact_phone: intake.emergency_contact_phone ?? null,
        emergency_contact_street_address:
          intake.emergency_contact_street_address ?? null,
        emergency_contact_city: intake.emergency_contact_city ?? null,
        emergency_contact_state: intake.emergency_contact_state ?? null,
        emergency_contact_zip_code: intake.emergency_contact_zip_code ?? null,

        // Insurance Information
        medicaid_id: (intake.medicaid_id as string | null | undefined) || null,

        // Notes
        patient_notes: intake.patient_notes || null,

        // Auto-formatted fields (SSN, ZIP, phone)
        ...formattedPatientFields,
      };

      // Filter to only include fields that can be set in the target form
      const filteredValues: Partial<TFieldValues> = {};
      for (const [key, value] of Object.entries(allValues)) {
        try {
          // Attempt to create a path - this will fail if the field doesn't exist
          filteredValues[key as keyof TFieldValues] =
            value as TFieldValues[keyof TFieldValues];
        } catch {
          // Field doesn't exist in target schema, skip it
        }
      }

      return filteredValues;
    },
    []
  );

  // Get formatted patient fields helper
  const getFormattedPatientFields = useCallback(() => {
    if (!selectedIntake) return {};
    const intake = selectedIntake as unknown as IntakeResponse;

    const formattedSSN = intake.patient_ssn
      ? formatSSN(intake.patient_ssn)
      : null;
    const formattedZip = intake.patient_zip_code
      ? formatZipCode(intake.patient_zip_code)
      : null;
    const formattedPhone = intake.patient_phone
      ? formatPhone(intake.patient_phone)
      : null;
    const formattedSecondaryPhone = intake.patient_secondary_phone
      ? formatPhone(intake.patient_secondary_phone)
      : null;
    const formattedEmergencyPhone = intake.emergency_contact_phone
      ? formatPhone(intake.emergency_contact_phone)
      : null;
    const formattedReferringPhone = intake.referring_phone
      ? formatPhone(intake.referring_phone)
      : null;
    const formattedPoaPhone = intake.poa_phone
      ? formatPhone(intake.poa_phone)
      : null;
    const formattedSurrogatePhone = intake.surrogate_phone
      ? formatPhone(intake.surrogate_phone)
      : null;

    // Return only fields that changed due to formatting
    const formattedValues: Record<string, string | null> = {};
    if (formattedSSN !== intake.patient_ssn && intake.patient_ssn)
      formattedValues.patient_ssn = formattedSSN;
    if (formattedZip !== intake.patient_zip_code && intake.patient_zip_code)
      formattedValues.patient_zip_code = formattedZip;
    if (formattedPhone !== intake.patient_phone && intake.patient_phone)
      formattedValues.patient_phone = formattedPhone;
    if (
      formattedSecondaryPhone !== intake.patient_secondary_phone &&
      intake.patient_secondary_phone
    )
      formattedValues.patient_secondary_phone = formattedSecondaryPhone;
    if (
      formattedEmergencyPhone !== intake.emergency_contact_phone &&
      intake.emergency_contact_phone
    )
      formattedValues.emergency_contact_phone = formattedEmergencyPhone;
    if (
      formattedReferringPhone !== intake.referring_phone &&
      intake.referring_phone
    )
      formattedValues.referring_phone = formattedReferringPhone;
    if (formattedPoaPhone !== intake.poa_phone && intake.poa_phone)
      formattedValues.poa_phone = formattedPoaPhone;
    if (
      formattedSurrogatePhone !== intake.surrogate_phone &&
      intake.surrogate_phone
    )
      formattedValues.surrogate_phone = formattedSurrogatePhone;

    return formattedValues;
  }, [selectedIntake]);

  // Update form data when intake is loaded or changes
  useEffect(() => {
    // Skip if no intake data
    if (!selectedIntake) return;

    // Cast to IntakeResponse since IntakeRecord is a subset of IntakeResponse
    const intake = selectedIntake as unknown as IntakeResponse;

    const formattedPatientFields = getFormattedPatientFields();

    // For existing patients with matched episode dates, SOC must be after matched start of care and discharge.
    const minSocDate = getMinSocDateForMatchedPatient(
      intake as IntakeResponse & {
        kinnser_matched_start_of_care?: string | null;
        kinnser_matched_discharge_date?: string | null;
      }
    );
    const defaultSocDate = minSocDate ?? getDefaultSocDate();
    // SOC is locked to matched episode start of care when (new episode + not discharged); do not overwrite.
    const matchedSoc = (intake as IntakeWithMatchedDates)
      .kinnser_matched_start_of_care;
    const socLockedToMatched =
      matchedSoc != null &&
      intake.kinnser_soc_date != null &&
      String(intake.kinnser_soc_date).split('T')[0] ===
        String(matchedSoc).split('T')[0];

    // Episode start is locked to day-after matched end when we have matched end date; do not overwrite.
    const matchedEnd = (intake as IntakeWithMatchedDates)
      .kinnser_matched_episode_end_date;
    const dayAfterMatchedEnd = matchedEnd
      ? getDayAfterDateStr(matchedEnd)
      : null;
    const episodeStartLockedToMatchedEnd =
      dayAfterMatchedEnd != null &&
      intake.kinnser_episode_start_date != null &&
      String(intake.kinnser_episode_start_date).split('T')[0] ===
        dayAfterMatchedEnd;

    // Check for auto-filled SOC date and episode start date (use default that respects matched dates when present)
    const autoFilledFields: Partial<CreatePatientFormData> = {};
    if (
      !socLockedToMatched &&
      (!intake.kinnser_soc_date ||
        (minSocDate && isOnOrBefore(intake.kinnser_soc_date, minSocDate)))
    ) {
      autoFilledFields.kinnser_soc_date = defaultSocDate;
    }
    const kinnserSocForEpisode =
      autoFilledFields.kinnser_soc_date ?? intake.kinnser_soc_date;
    if (
      !episodeStartLockedToMatchedEnd &&
      !intake.kinnser_episode_start_date &&
      kinnserSocForEpisode
    ) {
      autoFilledFields.kinnser_episode_start_date = kinnserSocForEpisode;
    }
    if (
      !intake.soc_date ||
      (minSocDate && isOnOrBefore(intake.soc_date, minSocDate))
    ) {
      autoFilledFields.soc_date = defaultSocDate;
    }
    // Soft-migration: prefer the shared `kinnser_episode_start_date` when
    // deciding whether to auto-fill the Roger form's episode start date, so
    // rows written via the new dual-write are respected and not overwritten.
    const existingRogerEpisodeStart =
      intake.kinnser_episode_start_date ?? intake.episode_start_date;
    if (
      !existingRogerEpisodeStart ||
      (minSocDate && isOnOrBefore(existingRogerEpisodeStart, minSocDate))
    ) {
      const rogerSocForEpisode = autoFilledFields.soc_date ?? intake.soc_date;
      if (rogerSocForEpisode) {
        autoFilledFields.episode_start_date = rogerSocForEpisode;
      }
    }

    // Combine all server updates
    const serverUpdates = { ...formattedPatientFields, ...autoFilledFields };

    // Only persist updates that actually differ from current intake to avoid
    // save→setSelectedIntake→effect re-run→save loop (hundreds of requests).
    const actualUpdates = filterActualUpdates(
      serverUpdates as Record<string, unknown>,
      intake as Record<string, unknown>
    ) as Partial<TFieldValues>;

    // Build form values filtered to only fields that exist in the target schema
    const formValues = buildFormValues(intake, formattedPatientFields);

    void (async () => {
      if (Object.keys(actualUpdates).length > 0) {
        // Await so auto-filled dates (and other fields) are persisted to the intake
        // before the user can switch tabs or click Schedule; otherwise the intake can
        // still have stale values when the schedule payload is built.
        await saveToServer(actualUpdates);
      }

      // Preserve unsaved local edits while still refreshing pristine fields
      // from the latest intake payload.
      reset(formValues as TFieldValues, {
        keepDirtyValues: true,
        keepErrors: true,
      });

      // Trigger validation after form is populated
      setTimeout(() => trigger(), 0);

      // Run fuzzy matching for language only if there's meaningful language data
      // Only applicable if the form has language fields
      const primaryLanguage = intake?.patient_primary_language?.trim();
      const languageUpdates: Record<string, string | null> = {};

      // Check if this form schema includes language fields before processing
      const hasLanguageFields =
        'kinnser_language_key' in formValues &&
        'kinnser_other_language' in formValues;

      if (hasLanguageFields) {
        if (primaryLanguage && primaryLanguage.length >= 2) {
          const languageMatcher = createFuzzyMatcher(LANGUAGE_OPTIONS, [
            'label',
          ]);
          const languageMatch = languageMatcher.findBestMatch(primaryLanguage);

          if (languageMatch) {
            console.log(
              `Auto-matching language "${primaryLanguage}" to "${languageMatch.label}"`
            );
            safeSetValue('kinnser_language_key', languageMatch.value);
            if (
              !formValues.kinnser_language_key ||
              formValues.kinnser_language_key === null
            ) {
              languageUpdates.kinnser_language_key = languageMatch.value;
            }
          } else {
            // No match found - set to Other/Unknown and use original language value
            console.log(
              `No language match for "${primaryLanguage}" - setting to Other/Unknown`
            );
            if (
              !formValues.kinnser_language_key ||
              formValues.kinnser_language_key === null
            ) {
              languageUpdates.kinnser_language_key = OTHER_LANGUAGE_KEY;
            }
            safeSetValue('kinnser_language_key', OTHER_LANGUAGE_KEY);
            if (
              !formValues.kinnser_other_language ||
              formValues.kinnser_other_language === null
            ) {
              languageUpdates.kinnser_other_language = primaryLanguage;
            }
            safeSetValue('kinnser_other_language', primaryLanguage);
          }
        } else {
          // No language data or insufficient length - set to Other/Unknown without logging
          safeSetValue('kinnser_language_key', OTHER_LANGUAGE_KEY);
          if (
            !formValues.kinnser_language_key ||
            formValues.kinnser_language_key === null
          ) {
            languageUpdates.kinnser_language_key = OTHER_LANGUAGE_KEY;
          }
          if (primaryLanguage) {
            if (
              !formValues.kinnser_other_language ||
              formValues.kinnser_other_language === null
            ) {
              languageUpdates.kinnser_other_language = primaryLanguage;
            }
            safeSetValue('kinnser_other_language', primaryLanguage);
          }
        }

        // Save language auto-matching results to server
        if (Object.keys(languageUpdates).length > 0) {
          console.log(
            'Updating fuzzy matched language key to server',
            languageUpdates
          );
          saveToServer(languageUpdates as Partial<TFieldValues>);
        }

        // Ensure patient_primary_language is consistent with the language dropdown selection
        // This implements the logic where dropdown changes drive patient_primary_language
        // Only process if patient_primary_language exists in this form schema
        if ('patient_primary_language' in formValues) {
          const finalLanguageKey = formValues.kinnser_language_key as
            string | null;
          if (finalLanguageKey && finalLanguageKey !== OTHER_LANGUAGE_KEY) {
            const languageOption = LANGUAGE_OPTIONS.find(
              option => option.value === finalLanguageKey
            );
            safeSetValue(
              'patient_primary_language',
              languageOption?.label || null
            );
          } else if (finalLanguageKey === OTHER_LANGUAGE_KEY) {
            safeSetValue(
              'patient_primary_language',
              (formValues.kinnser_other_language as string | null) || null
            );
          }
        }
      }
    })();
  }, [
    selectedIntake,
    reset,
    setValue,
    trigger,
    getFormattedPatientFields,
    saveToServer,
    buildFormValues,
    safeSetValue,
  ]);
}
